import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowLeft, User, MessageCircle, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const res = await axios.get(`${API}/blog/posts/${postId}`);
      setPost(res.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/blog/posts/${postId}/comments`);
      setComments(res.data.comments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/blog/posts/${postId}/comments`, {
        author_name: authorName,
        author_email: authorEmail,
        content: commentContent
      });
      setAuthorName('');
      setAuthorEmail('');
      setCommentContent('');
      fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-void-black min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-void-black min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-syne font-bold text-2xl text-white mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-neon-red hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const postVideos = post.videos || [];
  const postGallery = post.gallery || [];
  const postTags = post.tags || [];

  return (
    <div className="bg-void-black min-h-screen">
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" className="font-syne font-extrabold text-xl tracking-tighter uppercase">
            <span className="text-neon-red">Shadowcell</span> Studios
          </Link>
          <Link 
            to="/blog"
            className="flex items-center gap-2 font-manrope font-medium text-sm text-neutral-300 hover:text-neon-red transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.header 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {post.category && (
              <span className="inline-block px-3 py-1 bg-neon-red/20 text-neon-red text-sm font-manrope rounded mb-4">
                {post.category}
              </span>
            )}
            <h1 className="font-syne font-bold text-3xl md:text-5xl text-white leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-6 text-neutral-400">
              <div className="flex items-center gap-2">
                {post.author_picture ? (
                  <img src={post.author_picture} alt={post.author_name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <span className="font-manrope text-sm">{post.author_name}</span>
              </div>
              <span className="flex items-center gap-2 font-manrope text-sm">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
            </div>
          </motion.header>

          {/* Featured Image */}
          {post.featured_image && (
            <motion.div 
              className="mb-12 rounded-lg overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-auto"
              />
            </motion.div>
          )}

          {/* Content */}
          <motion.div 
            className="prose prose-invert prose-lg max-w-none mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="font-manrope text-neutral-300 leading-relaxed whitespace-pre-wrap">
              {post.content}
            </div>
          </motion.div>

          {/* Videos */}
          {postVideos.length > 0 && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="font-syne font-bold text-2xl text-white mb-6">Videos</h2>
              <div className="space-y-6">
                {postVideos.map((video, index) => (
                  <div key={index} className="rounded-lg overflow-hidden">
                    <video 
                      controls 
                      className="w-full"
                      poster={post.featured_image}
                    >
                      <source src={video} type="video/mp4" />
                    </video>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Gallery */}
          {postGallery.length > 0 && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <h2 className="font-syne font-bold text-2xl text-white mb-6">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {postGallery.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(image)}
                    className="aspect-square rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                  >
                    <img 
                      src={image} 
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Tags */}
          {postTags.length > 0 && (
            <motion.div 
              className="mb-12 pt-8 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex flex-wrap gap-2">
                {postTags.map((tag) => (
                  <span 
                    key={tag}
                    className="flex items-center gap-1 px-3 py-1 bg-white/5 text-neutral-400 text-sm font-manrope rounded-full"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Comments Section */}
          <motion.section 
            className="pt-8 border-t border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <h2 className="font-syne font-bold text-2xl text-white mb-8 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-neon-red" />
              Comments ({comments.length})
            </h2>

            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="glass p-6 rounded-lg mb-8">
              <h3 className="font-syne font-bold text-lg text-white mb-4">Leave a Comment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Your Name *"
                  required
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  className="bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope placeholder-neutral-500 focus:border-neon-red focus:outline-none"
                />
                <input
                  type="email"
                  placeholder="Your Email (optional)"
                  value={authorEmail}
                  onChange={(e) => setAuthorEmail(e.target.value)}
                  className="bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope placeholder-neutral-500 focus:border-neon-red focus:outline-none"
                />
              </div>
              <textarea
                placeholder="Your comment *"
                required
                rows={4}
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-white font-manrope placeholder-neutral-500 focus:border-neon-red focus:outline-none resize-none mb-4"
              />
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary px-6 py-3 rounded font-manrope font-bold text-sm uppercase flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </form>

            {/* Comments List */}
            {comments.length === 0 ? (
              <p className="text-neutral-500 font-manrope text-center py-8">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              <div className="space-y-6">
                {comments.map((comment) => (
                  <div key={comment.id} className="glass p-6 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-neon-red/20 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-neon-red" />
                      </div>
                      <div>
                        <p className="font-manrope font-bold text-white">{comment.author_name}</p>
                        <p className="font-manrope text-xs text-neutral-500">{formatDate(comment.created_at)}</p>
                      </div>
                    </div>
                    <p className="font-manrope text-neutral-300">{comment.content}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.section>
        </article>
      </main>

      {/* Lightbox */}
      {activeImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setActiveImage(null)}
        >
          <img 
            src={activeImage} 
            alt="Gallery"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-manrope text-neutral-500 text-sm">
            © {new Date().getFullYear()} ShadowCell Studios. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
